/**
 * DmsInstitutionSelect - Select from all institutions filtered by category
 *
 * Implements own fetch logic to support customOptions (prepended to fetched items).
 * For selecting from user's OWN institutions, use UserInstitutionSelect instead.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { intl, fetchData, getToken } from "dmsbasereact";
import { InstitutionCategoryEnum } from "../../constants/InstitutionType";
import { UserTypeEnum } from "../../constants/UserType";
import { DmsAutocomplete } from "../DmsAutoComplete/DmsAutoComplete";

export const DmsInstitutionSelect = ({
    label = null,
    value = null,
    onChange,
    multiple = false,
    required = false,
    disabled = false,
    fullWidth = true,
    category = null,
    userType = null,
    userTypes = null, // Support for multi-type (array)
    needsAuthentication = false,
    customOptions = [],
    error = false,
    helperText = null,
    sx = {},
    size = "medium",
    requireApproved = false, // If true, only show APPROVED institutions
    showAvailable = false, // If true, show APPROVED + PENDING institutions (excludes rejected/inactive)
    showCreateButton = false, // Show "Create Institution" button when NO-INSTITUTION selected
    onCreateInstitution = null, // Callback when create button is clicked
    onItemsLoaded = null, // Callback when items are loaded: (items) => void
}) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    // Determine category for URL
    // Supports both single userType and array of userTypes
    const categoryParam = useMemo(() => {
        if (category !== null && category !== undefined) {
            return `?category=${category}`;
        }

        // Convert userType/userTypes to array for unified handling
        let effectiveTypes = [];
        if (userTypes && Array.isArray(userTypes) && userTypes.length > 0) {
            effectiveTypes = userTypes;
        } else if (userType) {
            effectiveTypes = [userType];
        } else if (needsAuthentication) {
            const token = getToken();
            if (token?.user_type) {
                effectiveTypes = [token.user_type];
            } else if (token?.roles?.includes('DOCTOR')) {
                effectiveTypes = [UserTypeEnum.PRESCRIBER.value];
            } else if (token?.roles?.includes('PHARMACIST')) {
                effectiveTypes = [UserTypeEnum.DISPENSER.value];
            } else if (token?.roles?.includes('INSTITUTION_RESPONSIBLE')) {
                effectiveTypes = [UserTypeEnum.INSTITUTION_RESPONSIBLE.value];
            }
        }

        // Map types to categories
        const categories = new Set();
        effectiveTypes.forEach(type => {
            switch (type) {
                case UserTypeEnum.PRESCRIBER.value:
                case 'doctor':
                    categories.add(InstitutionCategoryEnum.Prescriber);
                    break;
                case UserTypeEnum.DISPENSER.value:
                case 'pharmacist':
                    categories.add(InstitutionCategoryEnum.Dispenser);
                    break;
                case UserTypeEnum.DISTRIBUTOR_RESPONSIBLE.value:
                case 'distributor_responsible':
                    categories.add(InstitutionCategoryEnum.Distributor);
                    break;
                case UserTypeEnum.INSTITUTION_RESPONSIBLE.value:
                case 'person':
                case 'institution_responsible':
                    // Institution Responsible can link to any institution type (except Distributor)
                    // Don't add specific category - will show all institutions (filtered in useEffect)
                    break;
                default:
                    break;
            }
        });

        // INSTITUTION_RESPONSIBLE can see all categories (post-fetch filters out Distributors)
        // Must check before size evaluation: when combined with DISPENSER, size is 1 (Dispenser only)
        // but INSTITUTION_RESPONSIBLE means no category filter should be applied
        const hasInstitutionResponsible = effectiveTypes.some(t =>
            t === UserTypeEnum.INSTITUTION_RESPONSIBLE.value ||
            t === 'person' ||
            t === 'institution_responsible'
        );
        if (hasInstitutionResponsible) {
            return "";
        }

        // If multiple categories, don't filter (show all)
        // If single category, filter by it
        if (categories.size === 1) {
            return `?category=${[...categories][0]}`;
        } else if (categories.size > 1) {
            // For multi-type users, we might need to show all relevant institutions
            // For now, return empty string to show all
            return "";
        }

        return "";
    }, [category, userType, userTypes, needsAuthentication]);

    // Determine endpoint based on filter props
    const endpoint = useMemo(() => {
        if (requireApproved) return "/institution/all/approved";
        if (showAvailable) return "/institution/all/available";
        return "/institution/all/public";
    }, [requireApproved, showAvailable]);
    const url = `${endpoint}${categoryParam}`;

    // Fetch institutions
    useEffect(() => {
        const fetchInstitutions = async () => {
            setLoading(true);
            setFetchError(null);
            try {
                const response = await fetchData(url, "GET", needsAuthentication);
                const data = response.data.data.rows || response.data.data;
                if (Array.isArray(data)) {
                    // Determine types that should NOT see Distributor institutions
                    const effectiveTypes = [];
                    if (userTypes && Array.isArray(userTypes) && userTypes.length > 0) {
                        effectiveTypes.push(...userTypes);
                    } else if (userType) {
                        effectiveTypes.push(userType);
                    }

                    // Filter out Distributor institutions (category 100) for non-distributor users
                    const shouldExcludeDistributors =
                        effectiveTypes.length > 0 &&
                        !effectiveTypes.includes(UserTypeEnum.DISTRIBUTOR_RESPONSIBLE.value) &&
                        !effectiveTypes.includes('distributor_responsible');

                    const filteredData = shouldExcludeDistributors
                        ? data.filter(item => {
                            const itemCategory = item.organization_branch?.category || item.category;
                            return itemCategory !== InstitutionCategoryEnum.Distributor;
                        })
                        : data;

                    const mappedItems = filteredData.map(item => ({
                        label: item.name,
                        value: item.organization_branch_pk,
                        institutionPk: item.pk, // Include institution.pk for FK mapping
                    })).sort((a, b) => a.label.localeCompare(b.label));
                    setItems(mappedItems);

                    // Call callback if provided
                    if (onItemsLoaded && typeof onItemsLoaded === 'function') {
                        onItemsLoaded(mappedItems);
                    }
                }
            } catch (err) {
                setFetchError(intl.get("FailedToLoadInstitutions").d("Failed to load institutions"));
                console.error("Error fetching institutions:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchInstitutions();
    }, [url, needsAuthentication, onItemsLoaded, userType, userTypes]);

    // Combine customOptions + fetched items
    const allOptions = useMemo(() =>
        [...customOptions, ...items],
        [customOptions, items]
    );

    const defaultLabel = multiple
        ? intl.get("Institutions").d("Institutions")
        : intl.get("Institution").d("Institution");

    const showAddButton = showCreateButton && onCreateInstitution && !disabled;

    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <DmsAutocomplete
                label={label || defaultLabel}
                value={multiple ? (Array.isArray(value) ? value : []) : (value ?? "")}
                onChange={onChange}
                options={allOptions}
                multiple={multiple}
                required={required}
                disabled={disabled}
                error={error || !!fetchError}
                helperText={helperText || fetchError}
                size={size}
                fullWidth={fullWidth}
                loading={loading}
                sx={{ flex: 1, ...sx }}
            />
            {showAddButton && (
                <Tooltip title={intl.get("CreateNewInstitution").d("Criar Nova Instituição")}>
                    <IconButton
                        color="primary"
                        onClick={onCreateInstitution}
                        sx={{
                            mt: size === "small" ? 0.5 : 1,
                            border: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: 1,
                            p: size === "small" ? 0.5 : 1,
                        }}
                    >
                        <AddCircleOutlineIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default DmsInstitutionSelect;
